// Shop Phone (Admin): the shop line's recent calls and the callers marked spam or priority (jobq-api
// "SHOP PHONE LINE"). Wording + calls: features/phone/shopPhone.js. Every call is securityFetch (the
// session's Admin PIN): a refused PIN (401 / 403) stops polling until the PIN is entered again, since each
// refused read counts against the shop's per-IP guess limit. A voicemail email links here with
// ?number=+1..., which fills in the Mark a caller form and highlights that caller's calls.

import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { AdminContext, requestAdminPin } from '../adminSession.jsx';
import { needsAdminPin } from '../utils/securityApi';
import { usePolling } from '../components/cameras/useCameraData';
import {
  CALLS_POLL_MS,
  CALL_DAYS,
  MARKS,
  callOutcome,
  callWhen,
  fetchShopCalls,
  markConfirmText,
  markInfo,
  markNumber,
  numberFromSearch,
  prettyNumber,
} from '../features/phone/shopPhone';

const TONE = {
  ok: { bg: '#d1f2dd', fg: '#14532d', border: '#16a34a' },
  bad: { bg: '#fde2e1', fg: '#7f1d1d', border: '#dc2626' },
  warn: { bg: '#fff1cc', fg: '#713f12', border: '#d97706' },
  muted: { bg: '#e9ecef', fg: '#495057', border: '#adb5bd' },
};

const styles = {
  page: { maxWidth: 1080 },
  small: { fontSize: 13 },
  badge: (tone) => ({ background: TONE[tone].bg, color: TONE[tone].fg, border: `1px solid ${TONE[tone].border}`,
    borderRadius: 6, padding: '1px 8px', fontWeight: 700, fontSize: 12, whiteSpace: 'nowrap' }),
  outcome: (tone) => ({ color: TONE[tone].fg, fontWeight: 600, fontSize: 14 }),
  transcript: { fontSize: 13, color: 'var(--bs-secondary-color, #6c757d)', maxWidth: 420 },
  highlight: { background: 'rgba(255, 193, 7, 0.18)' },
  markGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 12 },
};

function MarkBadge({ mark }) {
  if (!mark) return null;
  const info = markInfo(mark.kind);
  return <span style={styles.badge(info ? info.tone : 'muted')}>{info ? info.label : mark.kind}</span>;
}

export default function ShopPhonePage() {
  const admin = useContext(AdminContext);
  const location = useLocation();
  const linked = numberFromSearch(location.search);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [pinNeeded, setPinNeeded] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const [form, setForm] = useState({ number: linked || '', kind: 'priority', label: '' });

  useEffect(() => {
    if (linked) setForm((f) => ({ ...f, number: linked }));
  }, [linked]);

  const refused = useCallback((message) => {
    setPinNeeded(true);
    setError(message || 'JobQ refused the Admin PIN.');
  }, []);

  const load = useCallback(async () => {
    const r = await fetchShopCalls();
    if (needsAdminPin(r)) {
      refused(r.message);
      return;
    }
    if (!r.ok) {
      setError(r.message);
      return;
    }
    setError(null);
    setData(r.body);
  }, [refused]);

  // Every 30 s while the tab is visible; never while the PIN is refused.
  usePolling(load, CALLS_POLL_MS, !pinNeeded);

  const unlock = async () => {
    const user = await requestAdminPin();
    if (!user) return;
    if (admin && admin.loginAsAdmin) {
      admin.loginAsAdmin({ userId: user.userId, userName: user.userName, role: user.role, pin: user.pin },
        { remember: user.remember });
    } else {
      sessionStorage.setItem('userRole', String(user.role || '').toLowerCase());
      sessionStorage.setItem('adminPin', user.pin);
    }
    setError(null);
    setPinNeeded(false);
  };

  const mark = async (number, kind, label) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(markConfirmText(kind, number, label))) return false;
    setBusy(true);
    setMsg(null);
    const r = await markNumber(number, kind, label);
    setBusy(false);
    if (needsAdminPin(r)) {
      refused(r.message);
      return false;
    }
    if (!r.ok) {
      setMsg({ kind: 'danger', text: r.message });
      return false;
    }
    const info = markInfo(r.body && r.body.kind);
    const who = prettyNumber(r.body && r.body.number);
    setMsg({ kind: 'success', text: info ? `${who} is now ${info.label.toLowerCase()}. ${info.what}` : `${who}'s mark is cleared.` });
    load();
    return true;
  };

  const submitForm = async (e) => {
    e.preventDefault();
    if (!form.number.trim()) return;
    if (await mark(form.number.trim(), form.kind, form.label.trim())) setForm({ number: '', kind: 'priority', label: '' });
  };

  const calls = (data && data.calls) || [];
  const numbers = (data && data.numbers) || [];
  const priority = numbers.filter((n) => n.kind === 'priority');
  const spam = numbers.filter((n) => n.kind === 'spam');
  const cell = data && data.priority_forward;
  const linkedE164 = linked && calls.find((c) => c.number && prettyNumber(c.number) === prettyNumber(linked))?.number;

  return (
    <div className="container py-4" style={styles.page}>
      <h2 className="mb-1">Shop Phone</h2>
      <p className="text-muted mb-3" style={styles.small}>
        {data ? <>The shop line is <strong>{prettyNumber(data.shop_number)}</strong>. </> : null}
        Mark a caller as <strong>spam</strong> to give them a busy signal from now on, or <strong>priority</strong> to
        ring the desk and {cell ? <>Jesse's cell (<strong>{prettyNumber(cell)}</strong>)</> : "Jesse's cell"} together,
        at any hour. Jesse hears who it is and presses 1 to take it.
        {data && !cell && <span className="text-danger"> No cell number is set, so priority calls ring the desk only.</span>}
      </p>

      {error && (
        <div className="alert alert-warning d-flex align-items-center justify-content-between gap-2">
          <span>{error}</span>
          {pinNeeded && <button type="button" className="btn btn-sm btn-primary" onClick={unlock}>Enter Admin PIN</button>}
        </div>
      )}
      {msg && <div className={`alert alert-${msg.kind}`} role="status">{msg.text}</div>}

      <div className="card mb-3">
        <div className="card-body">
          <h5 className="card-title mb-2">Mark a caller</h5>
          <form className="row g-2 align-items-end" onSubmit={submitForm}>
            <div className="col-sm-4">
              <label className="form-label mb-0" htmlFor="sp-number">Phone number</label>
              <input id="sp-number" className="form-control" value={form.number} placeholder="316-555-1234"
                onChange={(e) => setForm({ ...form, number: e.target.value })} />
            </div>
            <div className="col-sm-3">
              <label className="form-label mb-0" htmlFor="sp-kind">Mark as</label>
              <select id="sp-kind" className="form-select" value={form.kind}
                onChange={(e) => setForm({ ...form, kind: e.target.value })}>
                <option value="priority">Priority (ring Jesse's cell)</option>
                <option value="spam">Spam (busy signal)</option>
              </select>
            </div>
            <div className="col-sm-3">
              <label className="form-label mb-0" htmlFor="sp-label">Name (optional)</label>
              <input id="sp-label" className="form-control" value={form.label} maxLength={80}
                placeholder="Faith Technologies" onChange={(e) => setForm({ ...form, label: e.target.value })} />
            </div>
            <div className="col-sm-2">
              <button type="submit" className="btn btn-primary w-100" disabled={busy || !form.number.trim()}>Save</button>
            </div>
          </form>
          <div className="text-muted mt-1" style={styles.small}>
            The name is what Jesse hears on his cell ("Priority call from Faith Technologies").
          </div>
        </div>
      </div>

      <div style={styles.markGrid} className="mb-3">
        {[['priority', priority], ['spam', spam]].map(([kind, rows]) => (
          <div className="card" key={kind} data-testid={`marks-${kind}`}>
            <div className="card-body">
              <h5 className="card-title mb-1">{MARKS[kind].label} callers ({rows.length})</h5>
              <div className="text-muted mb-2" style={styles.small}>{MARKS[kind].what}</div>
              {rows.length === 0 && <div className="text-muted" style={styles.small}>None yet.</div>}
              {rows.map((n) => (
                <div key={n.number} className="d-flex justify-content-between align-items-center border-top py-1">
                  <div>
                    <strong>{n.label || prettyNumber(n.number)}</strong>
                    {n.label && <span className="text-muted ms-2" style={styles.small}>{prettyNumber(n.number)}</span>}
                    {n.marked_by_name && <div className="text-muted" style={styles.small}>by {n.marked_by_name}</div>}
                  </div>
                  <button type="button" className="btn btn-sm btn-outline-secondary" disabled={busy}
                    onClick={() => mark(n.number, null, n.label)}>Clear</button>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      <div className="card">
        <div className="card-body">
          <h5 className="card-title mb-2">Calls, last {CALL_DAYS} days</h5>
          {!data && !error && <div className="text-muted">Loading…</div>}
          {data && calls.length === 0 && <div className="text-muted">No calls yet.</div>}
          {calls.length > 0 && (
            <div className="table-responsive">
              <table className="table table-sm align-middle mb-0">
                <thead>
                  <tr><th>Caller</th><th>What happened</th><th>Message</th><th /></tr>
                </thead>
                <tbody>
                  {calls.map((c) => {
                    const out = callOutcome(c);
                    const inbound = c.direction === 'inbound';
                    const hl = linkedE164 && c.number === linkedE164;
                    return (
                      <tr key={c.call_sid} style={hl ? styles.highlight : undefined} data-testid={`call-${c.call_sid}`}>
                        <td>
                          <div className="d-flex align-items-center gap-2">
                            <strong>{(c.mark && c.mark.label) || prettyNumber(c.number || (inbound ? c.from_number : c.to_number)) || 'Unknown caller'}</strong>
                            <MarkBadge mark={c.mark} />
                          </div>
                          {c.mark && c.mark.label && <div style={styles.small}>{prettyNumber(c.number)}</div>}
                          <div className="text-muted" style={styles.small}>{callWhen(c)}</div>
                        </td>
                        <td><span style={styles.outcome(out.tone)}>{out.text}</span></td>
                        <td style={styles.transcript}>{c.transcript || ''}</td>
                        <td className="text-end" style={{ whiteSpace: 'nowrap' }}>
                          {inbound && c.number && (
                            <>
                              {(!c.mark || c.mark.kind !== 'priority') && (
                                <button type="button" className="btn btn-sm btn-outline-success me-1" disabled={busy}
                                  onClick={() => {
                                    // eslint-disable-next-line no-alert
                                    const label = window.prompt('Name Jesse hears for this caller (optional):', (c.mark && c.mark.label) || c.caller_name || '');
                                    if (label === null) return;
                                    mark(c.number, 'priority', label.trim());
                                  }}>Priority</button>
                              )}
                              {(!c.mark || c.mark.kind !== 'spam') && (
                                <button type="button" className="btn btn-sm btn-outline-danger me-1" disabled={busy}
                                  onClick={() => mark(c.number, 'spam', c.caller_name || '')}>Spam</button>
                              )}
                              {c.mark && (
                                <button type="button" className="btn btn-sm btn-outline-secondary" disabled={busy}
                                  onClick={() => mark(c.number, null, c.mark.label)}>Clear</button>
                              )}
                            </>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
